import { generateId } from "../../utils/generateId";
import {
  createEntityAdapter,
  createSlice,
  EntityState,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../rootReducer";
import { Collection } from "../../models/collection";
import { deleteCollectionAction } from "../actions";
import { makeSyncUpsert, makeSyncRemove } from "../syncUpsert";

export const collectionAdapter = createEntityAdapter<Collection>();
type CollectionsState = EntityState<Collection, string>;
const initialState: CollectionsState = collectionAdapter.getInitialState();

// Next free position at the end of the collection list.
const nextSortOrder = (state: CollectionsState): number =>
  Object.values(state.entities).reduce(
    (max, c) => (c && !c.deletedAt ? Math.max(max, c.sortOrder ?? 0) : max),
    -1,
  ) + 1;

type CollectionPayload = {
  // Optional so the caller can pre-generate the id (needed when a cover image is
  // picked at create time — PhotoPicker stages the file under this id before the
  // row exists). Falls back to a fresh id when omitted.
  id?: string;
  name: string;
  description: string | null;
};

export const collectionsSlice = createSlice({
  name: "collections",
  initialState,
  reducers: {
    upsertManyFromSync: makeSyncUpsert(collectionAdapter),
    removeManyFromSync: makeSyncRemove(collectionAdapter),
    addCollection(
      state: CollectionsState,
      action: PayloadAction<CollectionPayload>,
    ): void {
      const { name, description } = action.payload;

      // Use the caller-supplied id (image-on-create path) or generate one.
      const id: string = action.payload.id ?? generateId();

      // Add collection — appended to the end of the manual order.
      collectionAdapter.addOne(state, {
        id,
        name,
        description: description ?? undefined,
        sortOrder: nextSortOrder(state),
      });
    },
    // Write a new manual order: sortOrder = position in collectionIds.
    reorderCollections(
      state: CollectionsState,
      action: PayloadAction<{ collectionIds: string[] }>,
    ): void {
      const updates = action.payload.collectionIds
        .map((id, index) => ({ id, index }))
        .filter(({ id, index }) => {
          const c = state.entities[id];
          return c && !c.deletedAt && c.sortOrder !== index;
        })
        .map(({ id, index }) => ({ id, changes: { sortOrder: index } }));
      collectionAdapter.updateMany(state, updates);
    },
    // Edit a collection's name and description together.
    updateCollectionDetails(
      state: CollectionsState,
      action: PayloadAction<{ id: string; name: string; description?: string }>,
    ): void {
      const { id, name, description } = action.payload;
      collectionAdapter.updateOne(state, {
        id,
        changes: {
          name,
          description: description?.trim() ? description.trim() : undefined,
        },
      });
    },
    // Stage the user's freshly-picked photo locally and queue it for upload
    // (dirty-tracked by reference → the next sync runs the upload).
    setCollectionImage(
      state: CollectionsState,
      action: PayloadAction<{ id: string; localImageUri: string }>,
    ): void {
      collectionAdapter.updateOne(state, {
        id: action.payload.id,
        changes: {
          localImageUri: action.payload.localImageUri,
          imagePendingUpload: true,
        },
      });
    },
    // Called by the SyncManager once the local image is uploaded to Storage:
    // store the remote URL and clear the pending flag (dirty-tracked → the URL
    // pushes on the same sync).
    setCollectionImageUploaded(
      state: CollectionsState,
      action: PayloadAction<{ id: string; url: string }>,
    ): void {
      collectionAdapter.updateOne(state, {
        id: action.payload.id,
        changes: {
          image: action.payload.url,
          imagePendingUpload: false,
          localImageUri: undefined,
        },
      });
    },
    // Records the local cache path for a downloaded remote image. Local-only
    // (omitted from sync); tagged fromSync so it isn't dirty-tracked/re-pushed.
    setCollectionCachedImage: {
      reducer(
        state: CollectionsState,
        action: PayloadAction<{ id: string; cachedImageUri: string }>,
      ) {
        collectionAdapter.updateOne(state, {
          id: action.payload.id,
          changes: { cachedImageUri: action.payload.cachedImageUri },
        });
      },
      prepare(payload: { id: string; cachedImageUri: string }) {
        return { payload, meta: { fromSync: true } };
      },
    },
    // Give up on an unrecoverable local pick (its file is gone, e.g. deleted by
    // iOS): drop the dead reference and the pending flag so the row settles and
    // sync stops retrying forever. Local-only fields, so tag fromSync (no push).
    clearCollectionPendingImage: {
      reducer(state: CollectionsState, action: PayloadAction<{ id: string }>) {
        collectionAdapter.updateOne(state, {
          id: action.payload.id,
          changes: { imagePendingUpload: false, localImageUri: undefined },
        });
      },
      prepare(payload: { id: string }) {
        return { payload, meta: { fromSync: true } };
      },
    },
  },
  extraReducers: (builder) => {
    // Soft-delete the collection; recipeCollections cascades its join rows
    // off the same deleteCollectionAction.
    builder.addCase(deleteCollectionAction, (state, action) => {
      const now = new Date().toISOString();
      collectionAdapter.updateOne(state, {
        id: action.payload,
        changes: { deletedAt: now },
      });
    });
  },
});

const collectionSelectors = collectionAdapter.getSelectors(
  (state: RootState) => state.collections,
);

export const selectCollectionById = collectionSelectors.selectById;

// selectAll* hides soft-deleted rows; sync reads entities directly. Ordered by the
// user's manual order (name as a tiebreak for un-ordered/legacy collections), so
// every collection listing — tabs, home rail, meal-plan picker — matches.
export const selectAllCollections = createSelector(
  collectionSelectors.selectAll,
  (collections) =>
    collections
      .filter((c) => !c.deletedAt)
      .sort((a, b) => {
        const diff = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
        return diff !== 0 ? diff : a.name.localeCompare(b.name);
      }),
);

export const {
  addCollection,
  reorderCollections,
  updateCollectionDetails,
  setCollectionImage,
  setCollectionImageUploaded,
  setCollectionCachedImage,
  clearCollectionPendingImage,
  upsertManyFromSync: upsertCollectionsFromSync,
  removeManyFromSync: removeCollectionsFromSync,
} = collectionsSlice.actions;

export default collectionsSlice.reducer;
